/**
 * This library used to lazy load ninja forms.
 *
 * The form content loaded by Ajax, dependencies needed to render the form composed in a way
 * to support a load of multiple forms on the page
 */
import { ResourceLoader } from '../utils/resource-loader';
import { isMaybeCloseToView, isInViewport } from '../utils/tools';
import { NFTPFormLoader, NFTPFormProcessor } from './contact-forms-lazy-load/nf-tp';
import { emailMeFormLoader, emailMeFormProcessor, maybeInitEmailMePopup, registerKeyBoardFocusHandler } from './contact-forms-lazy-load/email-me';

const evalExtraScriptData = function (script, redirectId, postId) {
	if (script.extra && script.extra.data) {
		// Indirect eval works in the global scope rather than the local scope
		// eslint-disable-next-line no-eval
		const geval = eval;
		const nfForms = null;
		const nfMPSettings = null;

		geval(script.extra.data);
		if (nfForms) {
			window.nfForms = window.nfForms.concat(nfForms);
		}

		if (redirectId) {
			window.nfForms[window.nfForms.length - 1].extra = { redirect_id: redirectId };
		}

		if (postId) {
			window.nfForms[window.nfForms.length - 1].extra = { post_id: postId };
		}
		window.nfFrontEnd = window.nfFrontEnd || nfFrontEnd;
		// needed to support multi-part forms
		window.nfMPSettings = window.nfMPSettings || nfMPSettings;
	}
};

const allFormModules = function () {
	return $('.nf_tp_module, .et_pb_email_attorney');
};

/**
 * Get settled promises
 * TODO remove the es6-promise and replace this method with Promise.getSettled
 */
const getSettled = function(promises) {
	return promises.map(p => p.then((element) => {
		element.status = 'fulfilled';
		return element;
	}).catch((error) => {
		error.status = 'rejected';
		return error;
	}));
};

/**
 * NF_TP and Email_Me forms lazy loading
 */
const loadAllForms = function () {
	// There is a chance that this function will be called twice, for example: ninja form is in viewport
	// and scroll event triggered in the same time
	// So, preventing the script from running POST queries twice
	if (loadAllForms.loaded) {
		return;
	}

	loadAllForms.loaded = true;

	const resourceLoader = new ResourceLoader();
	const loadPromises = [];
	allFormModules().each(function () {
		const element = $(this);
		const formLoader = element.hasClass('nf_tp_module') ? NFTPFormLoader(element) : emailMeFormLoader(element);
		loadPromises.push(formLoader);
	});

	Promise.all(getSettled(loadPromises)).then((formsData) => {
		let nfLibraryScript = null;
		formsData.forEach((formData) => {
			if (formData.status === 'rejected') {
				return;
			}
			if (formData.element.hasClass('nf_tp_module')) {
				NFTPFormProcessor(formData);
			} else {
				emailMeFormProcessor(formData);
			}
			// since the last item is always the ninja-form
			// and there is a chance that we have two or more forms on a page
			// we need to load libraries needed by these forms BEFORE we load the main ninja-form file
			// so extracting this script from scripts of actual response
			// In the same time we should also remember about extra variables stored in this dependency
			nfLibraryScript = formData.response.scripts.pop();
			evalExtraScriptData(nfLibraryScript, formData.element.data('redirect_id'), formData.element.data('post_id'));
			nfLibraryScript.extra = null;
			formData.response.styles.forEach(stylesheet => resourceLoader.insertStylesheet(stylesheet.src));
		});
		formsData.forEach((formData) => {
			if (formData.status === 'rejected') {
				return;
			}
			formData.response.scripts.forEach((script) => {
				evalExtraScriptData(script);
				resourceLoader.scheduleScript(script.src);
			});
		});
		resourceLoader.scheduleScript(nfLibraryScript.src);
		resourceLoader.loadScripts(() => {
			// We need to call resize after the form is rendered
			// to recalculate the parallax bg properties
			$(window).resize();
			document.dispatchEvent(new CustomEvent('all-nf-forms-loaded'));
		});
	}).catch(e => console.log(e));
};

const registerChangeFocusEvent = () => {
	$(document).on('nfFormReady', (e, layout) => {
		const layoutId = $(layout.$el[0]);
		const currentNFModule = $(layoutId).parent();
		let preloader = null;
		let formContainer = null;
		let removePreloader = true;
		if (currentNFModule.hasClass('nf_tp_module')) {
			// Check preloader for NF TP form
			preloader = currentNFModule.prev('span[id^=nf_tp_preloader_nf_tp_module_]');
			formContainer = currentNFModule;
		} else {
			// Check preloader for Email Me form
			preloader = $('span[id=email_me_preloader]');
			formContainer = $('#email_attorney_content');
		}

		//Check whether preloading text having keyboard focus
		if ($(preloader).is(':focus')) {
			const firstInput = $(formContainer).find('.nf-form-layout form input').first();
			if ($(firstInput).length === 0) {
				removePreloader = false;
			} else {
				$(firstInput).focus();
			}
		}
		if (removePreloader) {
			$(preloader).remove();
			if (document.getElementsByClassName('ENABLE_EMAIL_ME_CONTACT_FORM_POP_UP_FIX').length !== 0) {
				if ((formContainer[0].id) === 'email_attorney_content') {
					registerKeyBoardFocusHandler();
				}
			}
		}
	});
};

const allFormsFieldsUpdate = () => {
	$(document).on('nfFormReady', () => {
		//added aria hidden for * symbol
		$('.ninja-forms-req-symbol').each(function() {
			$(this).attr('aria-hidden', true);
		});
		//Handling the aria-invalid attribute according to the mandatory fields
		$('.nf-field-element input, .nf-field-element select, .nf-field-element textarea').each(function() {
			if (!$(this).prop('required')) {
				$(this).removeAttr('aria-invalid');
				$(this).removeAttr('aria-describedby');
			} else {
				$(this).attr('aria-invalid', false);
			}
			$(this).removeAttr('aria-labelledby');
		});
		//Append the aria-label for privacy policy
		$('.nf-field-element .form-links a').each(function() {
			const linkInaTag = $(this).attr('href');
			if (linkInaTag.indexOf('privacy') !== -1) {
				$(this).attr('aria-label', 'Privacy Policy (opens in a new tab)');
			}
		});
		//Append the aria-label for radio group
		$('.listradio-wrap ul').each(function() {
			$(this).attr('role', 'radiogroup');
			$(this).find('li label').css('display', 'inline-block');
		});
		//radio button attribute changes
		$('.listradio-wrap ul li input').each(function() {
			$(this).removeAttr('tabindex');
		});
		//Remove autocomplete attribute
		$('.nf-field-element textarea').each(function() {
			$(this).removeAttr('autocomplete');
		});
	});
};

//Apply autocomplete attribute according to the value
const FormsFieldsAutocompleteUpdate = (selector, labelFieldName, attributeValue) => {
	let index = 1;
	$(selector).each(function() {
		const currentLabelText = $(this).text().trim().toLowerCase();
		if ((currentLabelText.indexOf(labelFieldName)) !== -1) {
			const currentFieldText = $(this).closest('.nf-field-label').next('.nf-field-element').children('input');
			const autocompleteValue = labelFieldName === 'address' ? `${attributeValue}${index}` : attributeValue;
			index++;
			currentFieldText.attr('autocomplete', autocompleteValue);
		}
	});
};

const FormsFieldsScreenReaderFixes = () => {
	//Code for resolve Screen reader problem
	$('.listcheckbox-wrap .nf-field-label span').each(function() {
		const currentLabelId = $(this).attr('id');
		const checkboxMainDiv = $(this).closest('.nf-field-label').next('.nf-field-element');
		if (checkboxMainDiv.length) {
			checkboxMainDiv.attr('role', 'group');
			checkboxMainDiv.attr('aria-labelledby', currentLabelId);
			checkboxMainDiv.find('ul li input').attr('style', 'width: 1px !important');
			checkboxMainDiv.find('ul li label').css('display', 'inline-block');
		}
	});
};

const allFormsFieldsUpdateAutocomplete = () => {
	$(document).on('nfFormReady', () => {
		FormsFieldsAutocompleteUpdate('.nf-field-label label', 'address', 'address-line');
		FormsFieldsAutocompleteUpdate('.nf-field-label label', 'company', 'organization');
		FormsFieldsScreenReaderFixes();
	});
};

const appendPreloader = (nfFormContainer) => {
	const preloaderElement = $('<span tabindex="0" class="nf_preloader">Loading Form...</span>');
	if ($(nfFormContainer).hasClass('nf_tp_module')) {
		// Preloading text for every NF TP forms
		const NFTPModuleId = $(nfFormContainer).attr('id');
		const preloaderId = `nf_tp_preloader_${NFTPModuleId}`;
		preloaderElement.attr('id', preloaderId);
		$(nfFormContainer).before(preloaderElement);
		return;
	}
	if ($(nfFormContainer).hasClass('et_pb_email_attorney')) {
		// Preloading text for Email Me forms
		// It's not necessary to specify Email Me form ID since there is only one div container for Email Me forms loading
		const preloaderId = 'email_me_preloader';
		preloaderElement.attr('id', preloaderId);
		if ($(`#${preloaderId}`).length === 0) {
			$('#email_attorney_content').prepend(preloaderElement);
		}
	}
};

const initAllForms = () => {
	const inViewPortHandlers = [];
	loadAllForms.loaded = false;
	maybeInitEmailMePopup();

	allFormModules().each(function () {
		const showIfInViewPort = () => {
			if (isMaybeCloseToView(this, 2000) || isInViewport(this)) {
				inViewPortHandlers.forEach(handler => document.removeEventListener('scroll', handler));
				loadAllForms();
			}
		};
		inViewPortHandlers.push(showIfInViewPort);
		appendPreloader(this);
	});
	inViewPortHandlers.forEach((handler) => {
		document.addEventListener('scroll', handler);
		handler();
	});
	registerChangeFocusEvent();
	if (coreAccessibility.enable_nf_fields_update_a11y) {
		allFormsFieldsUpdate();
	}
	if (coreAccessibility.enable_nf_fields_update_autocomplete_a11y) {
		allFormsFieldsUpdateAutocomplete();
	}
};

const init = () => {
	/**
	 * nfForms it is a global variable that used by ninja-forms. This array contains all forms needed on the page.
	 */
	if (typeof window.nfForms === 'undefined') {
		window.nfForms = [];
	}
	if (typeof window.flNfFormsLoaded === 'undefined') {
		window.flNfFormsLoaded = {};
	}

	$(document).ready(() => {
		initAllForms();
	});
};

export default init;



// WEBPACK FOOTER //
// ./wp-content/plugins/core/assets/theme/js/src/forms/contact-forms-lazy-load.js